// src/components/dashboard/RefillCard.jsx
import React, { useMemo, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { useJugImageLogic } from "../../../hooks/useJugImageLogic";

const RefillCard = ({
  oldJug, // Botellón que se retorna
  newJug, // Botellón nuevo del recambio
  isSelected,
  onSelection,
  onPayment,
  canSelect = false,
}) => {
  const { getJugImage } = useJugImageLogic();
  const jugOldImagePath = getJugImage(oldJug);
  const jugNewImagePath = getJugImage(newJug);
  // Configuración del botellón viejo (izquierda)
  const oldJugConfig = useMemo(() => {
    const daysInUse = oldJug.days_since_delivery;
    const alertType = oldJug.alert_type;

    if (alertType === "mandatory_return") {
      return {
        label: "Retorno necesario",
        bgColor: "bg-red-700",
        textColor: "text-orange-100",
        bottleOpacity: 0,
      };
    } else if (alertType === "return_required") {
      return {
        label: "Para retornar",
        bgColor: "bg-neutral-700",
        textColor: "text-neutral-200",
        bottleOpacity: 20,
      };
    } else {
      return {
        label: "Para retornar",
        bgColor: "bg-neutral-700",
        textColor: "text-neutral-200",
        bottleOpacity: 20,
      };
    }
  }, [oldJug]);

  // Configuración del botellón nuevo (derecha)
  const newJugConfig = useMemo(() => {
    const status = newJug.order_detail_status;
    const paymentStatus = newJug.payment_status;
    const needsPayment =
      paymentStatus === "pending" || paymentStatus === "failed";

    // Calcular tiempo estimado
    const getEstimatedTime = () => {
      if (!newJug.scheduled_delivery_date) return null;

      const now = new Date();
      const scheduled = new Date(newJug.scheduled_delivery_date);
      const diffMs = scheduled.getTime() - now.getTime();
      const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

      return diffHours > 0 ? diffHours : null;
    };

    const estimatedHours = getEstimatedTime();

    const configs = {
      recently_ordered: {
        label: "Recién pedido",
        bgColor: "bg-gray-200",
        textColor: "text-neutral-500",
        showPayButton: needsPayment,
        timeTag: needsPayment
          ? "Sin pago"
          : estimatedHours
          ? `Llega en ${estimatedHours}h`
          : null,
        timeTagColor: needsPayment
          ? "bg-red-700 text-rose-200"
          : "bg-zinc-100 text-neutral-800",
        bottleOpacity: needsPayment ? 40 : 100,
      },
      ready_for_production: {
        label: "En cola de producción",
        bgColor: "bg-sky-100",
        textColor: "text-blue-600",
        timeTag: estimatedHours ? `Llega en ${estimatedHours}h` : null,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showPayButton: false,
        bottleOpacity: 100,
      },
      in_washing: {
        label: "En cola de producción",
        bgColor: "bg-sky-100",
        textColor: "text-blue-600",
        timeTag: estimatedHours ? `Llega en ${estimatedHours}h` : null,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showPayButton: false,
        bottleOpacity: 100,
      },
      in_filling: {
        label: "En cola de producción",
        bgColor: "bg-sky-100",
        textColor: "text-blue-600",
        timeTag: estimatedHours ? `Llega en ${estimatedHours}h` : null,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showPayButton: false,
        bottleOpacity: 100,
      },
      ready_for_delivery: {
        label: "En camino",
        bgColor: "bg-sky-100",
        textColor: "text-blue-600",
        timeTag: estimatedHours ? `Llega en ${estimatedHours}h` : null,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showPayButton: false,
        bottleOpacity: 100,
      },
      in_transit: {
        label: "En camino",
        bgColor: "bg-sky-100",
        textColor: "text-blue-600",
        timeTag: estimatedHours ? `Llega en ${estimatedHours}h` : null,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showPayButton: false,
        bottleOpacity: 100,
      },
    };

    return configs[status] || configs["recently_ordered"];
  }, [newJug]);

  // IDs de botellones
  const oldJugId =
    oldJug.barcode || oldJug.detail_short_id || `${oldJug.jug_id?.slice(-8)}`;
  const newJugId =
    newJug.barcode || newJug.detail_short_id || `${newJug.jug_id?.slice(-8)}`;

  // Formatear monto para pago
  const formatAmount = useCallback((amount) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(amount);
  }, []);

  // Handlers
  const handleCardClick = () => {
    if (canSelect) {
      onSelection?.(newJug.jug_id, true);
    }
  };

  const handlePayClick = (e) => {
    e.stopPropagation();
    onPayment?.(newJug.jug_id, newJug.detail_total || newJug.order_total);
  };

  return (
    <div className="bg-white rounded-2xl flex justify-center items-center transition-all duration-200">
      {/* Botellón viejo - Para retornar (Izquierda) */}
      <div className="flex flex-col justify-center items-center gap-4 max-w-48 p-4 w-48">
        <div
          className={`w-fit h-fit flex items-center justify-center ${
            oldJugConfig.bottleOpacity < 100
              ? `opacity-${oldJugConfig.bottleOpacity}`
              : ""
          }`}
        >
          <img
            className="w-32 h-32 md:min-w-56 md:min-h-56"
            src={jugOldImagePath}
            alt={`Botellón ${oldJugId} para retornar`}
          />
        </div>

        <div className="self-stretch flex flex-col justify-center items-center gap-2">
          {/* Badge de estado del botellón viejo */}
          <div
            className={`px-2 py-1 ${oldJugConfig.bgColor} rounded-lg inline-flex justify-center items-center gap-2.5`}
          >
            <span
              className={`text-sm font-medium leading-none ${oldJugConfig.textColor}`}
            >
              {oldJugConfig.label}
            </span>
          </div>

          {/* ID y días en uso del botellón viejo */}
          <div className="inline-flex justify-start items-center gap-1 overflow-hidden">
            <span className="text-black text-base font-medium leading-none">
              {oldJugId}
            </span>
            <div className="px-1 py-0.5 bg-zinc-100 rounded flex justify-center items-center gap-2.5">
              <span className="text-center text-neutral-800 text-sm ">
                {oldJug.days_since_delivery}d en uso
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Icono de recambio (Centro) */}
      <div className="inline-flex flex-col justify-center items-center gap-1 w-fit">
        <div className="w-8 h-8 bg-sky-100 rounded-[32px] inline-flex justify-center items-center gap-2.5">
          <RefreshCw className="w-5 h-5 text-blue-600" />
        </div>
        <span className="text-blue-800 text-base font-medium leading-none">
          Recambio
        </span>
      </div>

      {/* Botellón nuevo (Derecha) */}
      <div className="flex flex-col justify-center items-center gap-4 p-4 max-w-48 w-48">
        {/* Imagen del botellón nuevo */}
        <div
          className={`w-fit h-fit  ${
            newJugConfig.bottleOpacity < 100
              ? `opacity-${newJugConfig.bottleOpacity}`
              : ""
          }`}
        >
          <img
            className="w-32 h-32 md:min-w-56 md:min-h-56"
            src={jugNewImagePath}
            alt={`Botellón nuevo ${newJugId}`}
          />
        </div>

        {/* Información del botellón nuevo */}
        <div className="self-stretch px-4 flex flex-col justify-center items-center gap-2">
          {/* Badge de estado del botellón nuevo */}
          <div
            className={`px-2 py-1 ${newJugConfig.bgColor} rounded-lg inline-flex justify-center items-center gap-2.5`}
          >
            <span
              className={`text-sm  font-medium leading-none ${newJugConfig.textColor}`}
            >
              {newJugConfig.label}
            </span>
          </div>
          {newJugConfig.showPayButton ? (
            <button
              onClick={handlePayClick}
              className="h-8 max-h-10 min-h-10 shadow-lg px-2 py-2 center bg-green-600 rounded-xl inline-flex justify-center items-center gap-1 hover:bg-green-700 transition-colors z-10"
              title={`Pagar ${formatAmount(
                newJug.detail_total || newJug.order_total
              )}`}
            >
              <span className="text-white text-base font-medium leading-none">
                Finalizar pago
              </span>
            </button>
          ) : (
            <div className="inline-flex justify-start items-center gap-1 overflow-hidden">
              <span className="text-black text-base font-medium leading-none">
                {newJugId}
              </span>
              {newJugConfig.timeTag && (
                <div
                  className={`px-1 py-0.5 rounded flex justify-center items-center gap-2.5 ${newJugConfig.timeTagColor}`}
                >
                  <span className="text-center text-sm ">
                    {newJugConfig.timeTag}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(RefillCard);
